import {useLayoutEffect, useRef, useState, type CSSProperties} from 'react'
import {gsap} from 'gsap'
import {useTheme} from '@primer/react-brand/esm'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'
import {getScrambleTextAnimation} from '@github-ui/brand-core/utils/animation'

import styles from './MonaFooterGrid.module.css'
import type {MonaFooterProps} from './types'
import {getMonaFooterLayoutSnapshot, useMonaFooterLayout} from './use-mona-footer-layout'

import tileGradient1 from './tile-gradient-1.webp'
import tileGradient2 from './tile-gradient-2.webp'

const BACKGROUNDS = ['#FF80D2', '#0FBF3E', '#C898FD'] as const
const HOVER_BACKGROUNDS = [...BACKGROUNDS, `url(${tileGradient1})`, `url(${tileGradient2})`]
const REPOSITORY_COLORS = [
  {
    background: 'var(--base-color-scale-purple-5)',
    lightName: 'var(--base-color-scale-purple-5)',
    darkName: 'var(--base-color-scale-purple-2)',
  },
  {
    background: 'var(--base-color-scale-green-4)',
    lightName: 'var(--base-color-scale-green-6)',
    darkName: 'var(--base-color-scale-green-3)',
  },
  {
    background: 'var(--base-color-scale-pink-3)',
    lightName: 'var(--base-color-scale-pink-6)',
    darkName: 'var(--base-color-scale-pink-2)',
  },
] as const

const COLOR_TILES_FIRST = [
  BACKGROUNDS[0],
  tileGradient1,
  BACKGROUNDS[1],
  BACKGROUNDS[2],
  tileGradient2,
  tileGradient1,
  BACKGROUNDS[0],
  BACKGROUNDS[1],
]

function createGridLayout(columns: number, rows: number) {
  let repositoryIndex = 0
  const cells = Array.from({length: columns * rows}, (_, index) => {
    const colIndex = index % columns
    const rowIndex = Math.floor(index / columns)
    let grayBackground = 'transparent'
    if ((colIndex + rowIndex) % 4 === 2) {
      grayBackground = 'var(--brand-color-border-muted)'
    } else if ((colIndex + rowIndex) % 4 === 1) {
      grayBackground = 'var(--brand-color-canvas-subtle)'
    }
    return {
      id: `cell-${colIndex}-${rowIndex}`,
      colIndex,
      rowIndex,
      isLastColumn: colIndex === columns - 1,
      isLastRow: rowIndex === rows - 1,
      repositoryIndex: grayBackground === 'transparent' ? repositoryIndex++ : null,
      grayStyle: {
        '--color': grayBackground,
      } satisfies CSSProperties & {'--color': string},
    }
  })
  const revealCells = cells
    .filter(cell => cell.repositoryIndex !== null)
    .sort(
      (a, b) =>
        a.colIndex + (rows - 1 - a.rowIndex) - (b.colIndex + (rows - 1 - b.rowIndex)) || b.rowIndex - a.rowIndex,
    )
  const colorCells =
    columns === 6 && rows === 4
      ? COLOR_TILES_FIRST.map((_, index) => {
          const colIndex = Math.ceil(index / 2)
          return {colIndex, rowIndex: rows - 1 - (index - colIndex)}
        })
      : columns === 4 && rows === 6
        ? [
            {colIndex: 0, rowIndex: 4},
            {colIndex: 0, rowIndex: 3},
            {colIndex: 1, rowIndex: 3},
            {colIndex: 1, rowIndex: 2},
            {colIndex: 2, rowIndex: 2},
            {colIndex: 2, rowIndex: 1},
            {colIndex: 3, rowIndex: 1},
            {colIndex: 3, rowIndex: 0},
          ]
        : [
            {colIndex: 0, rowIndex: 4},
            {colIndex: 0, rowIndex: 3},
            {colIndex: 1, rowIndex: 3},
            {colIndex: 1, rowIndex: 2},
            {colIndex: 2, rowIndex: 2},
            {colIndex: 2, rowIndex: 1},
          ]
  const colorTiles = colorCells.slice(0, COLOR_TILES_FIRST.length).map(({colIndex, rowIndex}, index) => ({
    color: COLOR_TILES_FIRST[index],
    col: colIndex,
    row: rowIndex,
  }))

  return {
    cells: cells
      .map(cell => {
        const colorTile = colorTiles.find(tile => tile.col === cell.colIndex && tile.row === cell.rowIndex)
        const color = colorTile?.color ?? 'transparent'
        const colorBackground = color === tileGradient1 || color === tileGradient2 ? `url(${color})` : color
        const repositoryColors = REPOSITORY_COLORS[(cell.rowIndex * 2 + cell.colIndex) % REPOSITORY_COLORS.length]
        if (!repositoryColors) throw new RangeError('Invalid repository color index')
        return {
          ...cell,
          repositoryStyle: {
            '--repository-background': repositoryColors.background,
            '--repository-name-light': repositoryColors.lightName,
            '--repository-name-dark': repositoryColors.darkName,
            '--repository-backing-top': cell.rowIndex === 0 ? 0 : -1,
            '--repository-backing-right': cell.isLastColumn ? 0 : -1,
            '--repository-backing-bottom': cell.isLastRow ? 0 : -1,
            '--repository-backing-left': cell.colIndex === 0 ? 0 : -1,
          } satisfies CSSProperties & Record<`--repository-${string}`, string | number>,
          colorStyle: {
            '--color': colorBackground,
          } satisfies CSSProperties & {'--color': string},
          hoverStyle: {
            '--color': HOVER_BACKGROUNDS[(cell.rowIndex * 2 + cell.colIndex) % HOVER_BACKGROUNDS.length],
          } satisfies CSSProperties & {'--color': string | undefined},
        }
      })
      // Stable repository DOM order preserves focus when only the grid coordinates change.
      .sort((a, b) => (a.repositoryIndex ?? cells.length) - (b.repositoryIndex ?? cells.length)),
    colorTiles,
    revealCells,
  }
}

const GRID_LAYOUTS = {
  desktop: createGridLayout(6, 4),
  tablet: createGridLayout(4, 6),
  mobile: createGridLayout(3, 6),
}

function repositoryHref(url: string) {
  const parsed = new URL(url, 'https://github.com')
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    throw new TypeError('Featured repository URLs must use HTTP or HTTPS')
  }
  return parsed.href
}

function createTileHoverAnimation(tile: HTMLElement, property: 'opacity' | '--opacity' = 'opacity') {
  const initialOpacity = tile.style.getPropertyValue(property)
  let tween: gsap.core.Tween | undefined
  let activeOpacity = 0
  let fadingIn = false
  const fadeOut = () => {
    tween = gsap.to(tile, {[property]: 0, duration: 0.3, delay: 0.5, ease: 'power2.out'})
  }

  return {
    setActiveOpacity(opacity: number) {
      if (opacity === activeOpacity) return
      const wasActive = activeOpacity > 0
      activeOpacity = opacity
      if (opacity > 0) {
        tween?.kill()
        fadingIn = true
        tween = gsap.to(tile, {
          [property]: opacity,
          duration: 0.3,
          ease: 'power2.out',
          onComplete: () => {
            fadingIn = false
            if (activeOpacity === 0) fadeOut()
          },
        })
      } else if (wasActive && !fadingIn) {
        fadeOut()
      }
    },
    dispose() {
      tween?.kill()
      tile.style.setProperty(property, initialOpacity)
    },
  }
}

export function MonaFooterGrid({
  animate = false,
  showFinalState = false,
  repos,
  onLayoutChange,
}: {
  animate?: boolean
  showFinalState?: boolean
  repos?: MonaFooterProps['repos']
  onLayoutChange?: () => void
}) {
  const {colorMode} = useTheme()
  const gridRef = useRef<HTMLDivElement>(null)
  const prefersReducedMotion = usePrefersReducedMotion()
  const layout = GRID_LAYOUTS[useMonaFooterLayout()]
  const previousLayoutRef = useRef<typeof layout | null>(null)
  const revealedRepositoriesRef = useRef(new Set<string>())
  const repositoriesReadyRef = useRef(false)
  const neighborAnimationsRef = useRef(new Map<HTMLElement, ReturnType<typeof createTileHoverAnimation>>())
  const [repositoriesReady, setRepositoriesReady] = useState(false)
  const [hoveredRepositoryIndex, setHoveredRepositoryIndex] = useState<number | null>(null)
  const [focusedRepositoryIndex, setFocusedRepositoryIndex] = useState<number | null>(null)
  const activeRepositoryCells = layout.cells.filter(
    cell =>
      cell.repositoryIndex !== null &&
      (cell.repositoryIndex === hoveredRepositoryIndex || cell.repositoryIndex === focusedRepositoryIndex),
  )

  useLayoutEffect(() => {
    // Hydration initially uses the server layout, not the browser's actual layout.
    const clientLayout = GRID_LAYOUTS[getMonaFooterLayoutSnapshot()]
    const previousLayout = previousLayoutRef.current
    previousLayoutRef.current = clientLayout
    if (previousLayout !== null && previousLayout !== clientLayout) {
      onLayoutChange?.()
    }
  }, [layout, onLayoutChange])

  useLayoutEffect(() => {
    const grid = gridRef.current
    if (!grid) return

    const revealedRepositories = revealedRepositoriesRef.current
    const panels = layout.revealCells.flatMap(cell => {
      const panel = grid.querySelector<HTMLElement>(`[data-cell="${cell.id}"] .${styles.RepositoryPanel}`)
      return panel ? [panel] : []
    })
    const nameCleanups: Array<() => void> = []

    const context = gsap.context(() => {
      const nameAnimations = new Map<HTMLElement, gsap.core.Timeline>()

      if (!prefersReducedMotion && (animate || showFinalState || repositoriesReadyRef.current)) {
        for (const panel of panels) {
          const name = panel.querySelector<HTMLElement>(`.${styles.RepositoryNameText}`)
          const text = name?.firstElementChild
          if (!name || !text) continue

          const animation = getScrambleTextAnimation(name, 0.6, {revealDelay: 0, entranceDelay: 0}).pause()
          animation.progress(1)
          nameAnimations.set(panel, animation)
          const hoverAnimation = getScrambleTextAnimation(name, 0.6, {revealDelay: 0, useMask: false}).pause()

          let isHovered = false
          let isFocused = false
          const handleInteraction = (event: Event) => {
            if (event instanceof PointerEvent && event.pointerType === 'touch') return

            const wasInteracting = isHovered || isFocused
            if (event.type === 'pointerenter') isHovered = true
            if (event.type === 'pointerleave') isHovered = false
            if (event.type === 'focusin') isFocused = true
            if (event.type === 'focusout') isFocused = false

            if (
              (isHovered || isFocused) &&
              !wasInteracting &&
              grid.getAttribute('data-repositories-ready') === 'true'
            ) {
              hoverAnimation.restart()
            }
          }

          panel.addEventListener('pointerenter', handleInteraction)
          panel.addEventListener('pointerleave', handleInteraction)
          panel.addEventListener('focusin', handleInteraction)
          panel.addEventListener('focusout', handleInteraction)
          nameCleanups.push(() => {
            panel.removeEventListener('pointerenter', handleInteraction)
            panel.removeEventListener('pointerleave', handleInteraction)
            panel.removeEventListener('focusin', handleInteraction)
            panel.removeEventListener('focusout', handleInteraction)
            animation.kill()
            hoverAnimation.kill()
            // The following sibling holds React's current accessible name and is never scrambled.
            const staticName = name.nextElementSibling
            if (staticName) text.textContent = staticName.textContent
          })
        }
      }

      const isRevealed = (panel: HTMLElement) => {
        const index = panel.getAttribute('data-repository-index')
        return index !== null && revealedRepositories.has(index)
      }
      const revealedPanels = panels.filter(isRevealed)
      if (revealedPanels.length > 0) gsap.set(revealedPanels, {autoAlpha: 1, attr: {'data-revealed': 'true'}})
      const colorTiles = layout.colorTiles.map(({col, row}) => `[data-cell="cell-${col}-${row}"] .${styles.ColorTile}`)
      if (showFinalState || prefersReducedMotion || repositoriesReadyRef.current) {
        gsap.set(colorTiles, {'--opacity': 0})
        if (panels.length > 0) gsap.set(panels, {autoAlpha: 1})
        gsap.set(grid, {attr: {'data-repositories-ready': 'true'}})
        setRepositoriesReady(true)
        return
      }
      if (!animate) return

      const tl = gsap.timeline({
        delay: 0.8,
      })
      tl.to(colorTiles, {
        '--opacity': 1,
        duration: 0.6,
        stagger: {each: 0.3, from: 'start'},
        ease: 'power2.out',
      })
      tl.to(colorTiles, {
        '--opacity': 0,
        duration: 0.6,
        stagger: {each: 0.1, from: 'start'},
        ease: 'power2.out',
      })
      const hiddenPanels = panels.filter(panel => !isRevealed(panel))
      if (hiddenPanels.length > 0) {
        const revealStart = tl.duration() - 0.8
        const revealStagger = 0.12

        tl.to(
          hiddenPanels,
          {
            autoAlpha: 1,
            duration: 0.6,
            stagger: revealStagger,
            ease: 'power2.out',
          },
          revealStart,
        )

        for (const [index, panel] of hiddenPanels.entries()) {
          const animation = nameAnimations.get(panel)
          if (animation) tl.add(animation.play(0), revealStart + index * revealStagger)
        }
      }
      tl.set(grid, {attr: {'data-repositories-ready': 'true'}})
      tl.call(() => setRepositoriesReady(true))
    }, grid)

    return () => {
      for (const panel of panels) {
        const index = panel.getAttribute('data-repository-index')
        if (index !== null && panel.style.visibility && panel.style.visibility !== 'hidden') {
          revealedRepositories.add(index)
        }
      }
      repositoriesReadyRef.current = grid.getAttribute('data-repositories-ready') === 'true'
      context.revert()
      for (const cleanup of nameCleanups) cleanup()
    }
  }, [animate, showFinalState, prefersReducedMotion, repos?.items, layout])

  useLayoutEffect(() => {
    const grid = gridRef.current
    if (!grid || prefersReducedMotion) return

    const cleanups: Array<() => void> = []
    for (const cell of grid.querySelectorAll<HTMLElement>(`.${styles.Rect}`)) {
      const tile = cell.querySelector<HTMLElement>(`.${styles.HoverTile}`)
      if (!tile) continue

      const animation = createTileHoverAnimation(tile)
      const enter = (event: PointerEvent) => {
        if (
          event.pointerType === 'touch' ||
          !window.matchMedia('(hover: hover)').matches ||
          grid.getAttribute('data-repositories-ready') !== 'true'
        )
          return
        animation.setActiveOpacity(1)
      }
      const leave = () => {
        animation.setActiveOpacity(0)
      }
      cell.addEventListener('pointerenter', enter)
      cell.addEventListener('pointerleave', leave)
      cleanups.push(() => {
        cell.removeEventListener('pointerenter', enter)
        cell.removeEventListener('pointerleave', leave)
        animation.dispose()
      })
    }

    const neighborAnimations = neighborAnimationsRef.current
    for (const tile of grid.querySelectorAll<HTMLElement>(`.${styles.NeighborTile}`)) {
      neighborAnimations.set(tile, createTileHoverAnimation(tile, '--opacity'))
    }

    return () => {
      for (const cleanup of cleanups) cleanup()
      for (const animation of neighborAnimations.values()) animation.dispose()
      neighborAnimations.clear()
    }
  }, [layout, prefersReducedMotion, repos?.items])

  useLayoutEffect(() => {
    if (!repositoriesReady) return

    for (const [tile, animation] of neighborAnimationsRef.current) {
      animation.setActiveOpacity(
        tile.getAttribute('data-neighbor-focused') === 'true'
          ? Number(getComputedStyle(tile).getPropertyValue('--neighbor-target-opacity'))
          : 0,
      )
    }
  }, [repositoriesReady, hoveredRepositoryIndex, focusedRepositoryIndex, layout, prefersReducedMotion, repos?.items])

  return (
    <div className={styles.MonaFooterGrid} data-repository-color-mode={colorMode} ref={gridRef}>
      {layout.cells.map(cell => {
        const repository = cell.repositoryIndex === null ? undefined : repos?.items[cell.repositoryIndex]
        return (
          <div
            key={cell.repositoryIndex === null ? cell.id : `repository-${cell.repositoryIndex}`}
            data-cell={cell.id}
            data-last-column={cell.isLastColumn}
            data-last-row={cell.isLastRow}
            className={styles.Rect}
            style={{gridColumn: cell.colIndex + 1, gridRow: cell.rowIndex + 1}}
          >
            <div className={styles.GrayTile} style={cell.grayStyle} />
            <div className={styles.ColorTile} style={cell.colorStyle} />
            {!repository && !prefersReducedMotion && (
              <>
                <div className={styles.HoverTile} style={cell.hoverStyle} aria-hidden="true" />
                <div
                  className={styles.NeighborTile}
                  style={cell.hoverStyle}
                  data-neighbor-focused={activeRepositoryCells.some(
                    active =>
                      Math.max(Math.abs(active.colIndex - cell.colIndex), Math.abs(active.rowIndex - cell.rowIndex)) ===
                      1,
                  )}
                  data-neighbor-diagonal={
                    !activeRepositoryCells.some(
                      active =>
                        Math.abs(active.colIndex - cell.colIndex) + Math.abs(active.rowIndex - cell.rowIndex) === 1,
                    )
                  }
                  aria-hidden="true"
                />
              </>
            )}
            {repository && repos && (
              <a
                className={styles.RepositoryPanel}
                data-repository-index={cell.repositoryIndex}
                style={cell.repositoryStyle}
                href={repositoryHref(repository.url)}
                onPointerEnter={event => {
                  if (event.pointerType !== 'touch' && window.matchMedia('(hover: hover)').matches) {
                    setHoveredRepositoryIndex(cell.repositoryIndex)
                  }
                }}
                onPointerLeave={() => setHoveredRepositoryIndex(null)}
                onPointerCancel={() => setHoveredRepositoryIndex(null)}
                onFocus={event => {
                  if (event.currentTarget.matches(':focus-visible')) setFocusedRepositoryIndex(cell.repositoryIndex)
                }}
                onBlur={() => setFocusedRepositoryIndex(null)}
              >
                <div className={styles.RepositoryContent}>
                  <p className={styles.RepositoryName}>
                    <span className={styles.RepositoryNameText} aria-hidden="true">
                      <span>{repository.nameWithOwner.replace('/', ' / ')}</span>
                    </span>
                    <span>{repository.nameWithOwner.replace('/', ' / ')}</span>
                  </p>
                  {repository.description && <p className={styles.RepositoryDescription}>{repository.description}</p>}
                  <p className={styles.RepositoryStars}>
                    {repos.starsTemplate.replaceAll('{count}', String(repository.newStars))}
                  </p>
                </div>
              </a>
            )}
          </div>
        )
      })}
    </div>
  )
}
